import React, { useEffect } from 'react'

import { Link } from 'gatsby'
import styled from 'styled-components'

import { ParagraphSmall, TitleXL } from 'src/components/blocks/Typography'
import {
  Body2,
  Paragraph,
  StyledLinkXS,
  TitleH3
} from 'src/components/blocks/Typography'
import SectionCorner from 'src/components/partials/SectionCorner'
import { captureEvents } from 'src/utilities/analyticsEvents'
import { Breakpoints } from 'src/utilities/Breakpoints'
import { BrandColors, BrandTokens } from 'src/utilities/Colors'
import { getUtmParam, utmParamsList } from 'src/utilities/utmUtils'

const FormTitle = styled(TitleH3)<{
  theme?: 'light' | 'dark'
}>`
  margin-bottom: 0.75em;

  em {
    font-style: normal;
    color: ${props =>
      props.theme === 'light'
        ? BrandColors.electric800
        : BrandColors.nectar900};
  }
`

const LogoLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1em;
`

const FormWrapper = styled.div<{
  theme?: 'light' | 'dark'
  showLogo?: boolean
  minHeight?: string
}>`
  padding: 2.5rem;
  margin-top: 2rem;
  background-color: ${props =>
    props.theme === 'light' ? BrandColors.white : BrandColors.midnight700};
  color: ${props =>
    props.theme === 'light' ? BrandColors.text : BrandColors.white};
  border-radius: ${BrandTokens.borderRadius};
  border: 2px solid ${BrandTokens.borderDark};
  box-shadow: ${BrandTokens.boxShadow16};
  position: relative;
  z-index: 2;
  min-width: 300px;
  min-height: ${props => props.minHeight};

  @media (min-width: ${Breakpoints.md}px) {
    margin-top: 0;
  }

  ${ParagraphSmall} {
    margin-top: 1rem;
    text-align: center;

    a {
      color: ${props =>
        props.theme === 'light'
          ? BrandColors.primary
          : BrandColors.electric300};
      text-decoration: underline;
      font-weight: 600;
    }
  }

  ${TitleXL} {
    position: relative;
    z-index: 2;
  }
`

const SignUpForm = ({
  cornerBgColor = BrandTokens.backgroundLight,
  cornerFgColor = BrandTokens.backgroundLight,
  fillUtms = false,
  formTitle = 'Create your <em>free</em> Flatfile account',
  formTitleAs = 'h2',
  hsFormId = '63933458-eb10-4970-9bd5-c3a326fb7791',
  hsPortalId = '4588656',
  hsSource,
  hsSource_version = 'v1',
  minHeight = '620px',
  showLogo = false,
  theme
}: {
  cornerBgColor?: string
  cornerFgColor?: string
  fillUtms?: boolean
  formTitle?: string
  formTitleAs?: string
  hsFormId: string
  hsPortalId?: string
  hsSource?: string
  hsSource_version?: string
  showBtnMessage?: boolean
  minHeight?: string
  showLogo?: boolean
  theme?: 'light' | 'dark'
}) => {
  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://js.hsforms.net/forms/embed/v2.js'
    script.async = true
    document.body.appendChild(script)

    script.addEventListener('load', () => {
      const formId = hsFormId
      const portalId = hsPortalId
      const source = hsSource
      const source_version = hsSource_version
      const region = 'na1'

      if (window.hbspt) {
        window.hbspt.forms.create({
          formId,

          onFormReady($form) {
            captureEvents({
              posthog: {
                data: {
                  formId,
                  portalId,
                  region,
                  source
                },
                eventName: 'Form ready'
              }
            })

            // Fill in UTM parameters from cookie
            if (fillUtms) {
              utmParamsList.forEach(param => {
                const value = getUtmParam(param)
                const inputElement = $form.querySelector(
                  `input[name="${param}"]`
                )

                if (inputElement) {
                  inputElement.value = value
                }
              })
            }

            // Center and full width submit button
            const formButton = $form.querySelector('input[type="submit"]')
            // formButton.style.width = '100%'
            // center button
            formButton.style.margin = '0 auto'
            formButton.style.borderRadius = '6px'
          },

          onFormSubmit($form) {
            const email = $form.querySelector('input[name="email"]').value
            const firstname = $form.querySelector(
              'input[name="firstname"]'
            ).value
            const lastname = $form.querySelector('input[name="lastname"]').value
            const company = $form.querySelector('input[name="company"]').value
            const jobTitle = $form.querySelector('select[name="job_role__c"]')
              ? $form.querySelector('select[name="job_role__c"]').value
              : ''
            const useCase = $form.querySelector('select[name="use_case"]')

            captureEvents({
              ga4EventName: 'contact_form_fill',
              linkedInConversionID: 14920793,
              posthog: {
                data: {
                  company,
                  email,
                  firstname,
                  formId,
                  jobTitle,
                  lastname,
                  portalId,
                  region,
                  source,
                  source_version,
                  useCase
                },
                eventName: 'Form submitted'
              },
              uetEventName: 'contact_us'
            })
          },

          portalId,
          region,
          target: '#hubSpotForm'
        })
      }
    })
  }, [])

  return (
    <FormWrapper theme={theme} minHeight={minHeight}>
      {/* <SectionCorner
        backgroundColor={cornerBgColor}
        foregroundColor={cornerFgColor}
        position='tr'
        size={{
          default: '75px',
          sm: '110px',
          lg: '130px'
        }}
      /> */}

      {showLogo && (
        <LogoLink to='/'>
          <img
            alt='Flatfile logo'
            src='/img/contentful/gL6Blz3k-flatfile.svg'
          />
        </LogoLink>
      )}

      {/* if FormTitle is not null, render it */}
      {formTitle && (
        <FormTitle
          as={formTitleAs}
          color={theme === 'light' ? BrandColors.text : BrandColors.white}
          className='formTitle'
          theme={theme}
        >
          <span dangerouslySetInnerHTML={{ __html: formTitle }} />
        </FormTitle>
      )}

      <div id='hubSpotForm'></div>
    </FormWrapper>
  )
}

export default SignUpForm
